import { ReactNode, ButtonHTMLAttributes } from 'react';
import classNames from 'classnames';

export type ButtonVariant = `primary` | `secondary` | `tertiary` | `link`;
export type ButtonSize = `xl` | `lg` | `md` | `sm` | `xs`;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
    primary: `btn-variant-primary`,
    secondary: `btn-variant-secondary`,
    tertiary: `btn-variant-tertiary`,
    link: `btn-variant-link`
};

const sizeTextClasses: Record<ButtonSize, string> = {
    xl: `btn-text-xl`,
    lg: `btn-text-lg`,
    md: `btn-text-md`,
    sm: `btn-text-sm`,
    xs: `btn-text-xs`
};

const linkSizeClasses: Record<ButtonSize, string> = {
    xl: `btn-link-xl`,
    lg: `btn-link-lg`,
    md: `btn-link-md`,
    sm: `btn-link-sm`,
    xs: `btn-link-xs`
};

export const Button = ({
    children,
    variant = `primary`,
    size = `md`,
    startIcon,
    endIcon,
    type = `button`,
    className,
    ...rest
}: ButtonProps) => (
    <button
        type={type}
        className={classNames(
            `btn-text-base disabled:opacity-40 disabled:cursor-not-allowed`,
            variantClasses[variant],
            variant === `link` ? linkSizeClasses[size] : sizeTextClasses[size],
            className
        )}
        {...rest}
    >
        {startIcon}
        {children}
        {endIcon}
    </button>
);

export type IconButtonVariant = `primary` | `secondary` | `tertiary`;
export type IconButtonSize = `xl` | `lg` | `sm` | `xs`;

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: IconButtonVariant;
    size?: IconButtonSize;
    dangerOnHover?: boolean;
}

const iconSizeClasses: Record<IconButtonSize, string> = {
    xl: `btn-icon-xl`,
    lg: `btn-icon-lg`,
    sm: `btn-icon-sm`,
    xs: `btn-icon-xs`
};

export const IconButton = ({
    children,
    variant = `primary`,
    size = `sm`,
    type = `button`,
    className,
    dangerOnHover,
    ...rest
}: IconButtonProps) => (
    <button
        type={type}
        className={classNames(
            `btn-icon-base disabled:opacity-40 disabled:cursor-not-allowed`,
            variantClasses[variant],
            iconSizeClasses[size],
            dangerOnHover && `btn-danger-on-hover`,
            className
        )}
        {...rest}
    >
        {children}
    </button>
);
